import moment from "moment-timezone";
import { DEFAULT_KITCHEN_TIMEZONE } from "../constants";

/**
 * Venue timezone helpers.
 *
 * All time-dependent logic (open/closed, slot generation, scheduled pickup
 * construction, promo day-parts, weekend surcharge) must evaluate against the
 * kitchen's IANA timezone rather than the customer's browser clock.
 *
 * The active kitchen timezone is stored in module state and set once when the
 * kitchen loads (see `fetchKitchenAsync`). This keeps call sites simple while
 * still allowing an explicit timezone to be passed where correctness requires a
 * specific instant (scheduled times, tests, etc.).
 */

let activeKitchenTimezone: string = DEFAULT_KITCHEN_TIMEZONE;

/** Returns true when `tz` is a valid IANA timezone known to moment-timezone. */
const isValidTimezone = (tz?: string | null): tz is string =>
  Boolean(tz && moment.tz.zone(tz));

/** Record the venue timezone for the current session (falls back to default). */
export const setKitchenTimezone = (tz?: string | null): void => {
  activeKitchenTimezone = isValidTimezone(tz) ? tz : DEFAULT_KITCHEN_TIMEZONE;
};

/**
 * Resolve the timezone to use: an explicit override when valid, otherwise the
 * session's active kitchen timezone, otherwise the default.
 */
export const getKitchenTimezone = (override?: string | null): string => {
  if (isValidTimezone(override)) {
    return override;
  }
  return activeKitchenTimezone || DEFAULT_KITCHEN_TIMEZONE;
};

/** Current moment in the kitchen timezone (optionally overriding the zone). */
export const nowInKitchen = (tz?: string | null): moment.Moment =>
  moment.tz(getKitchenTimezone(tz));
